import { cn } from '@/lib/utils';

interface OrgLike {
  id?: string;
  name?: string | null;
  logo?: string | null;
}

/**
 * The organization a project belongs to, from the project's point of view.
 *
 * A project delivered FOR a client shows the client, not the agency that owns
 * the record — that is the organization a reader recognises. Agency-owned work
 * with no distinct client shows the agency.
 */
export function projectOrg(project: {
  organization?: OrgLike | null;
  clientOrganization?: OrgLike | null;
  organizationId?: string;
}): OrgLike | null {
  const client = project.clientOrganization;
  if (client && client.id !== project.organizationId) return client;
  return project.organization ?? null;
}

export function projectOrgName(project: Parameters<typeof projectOrg>[0]): string {
  return projectOrg(project)?.name ?? '';
}

const SIZES = {
  sm: 'h-6 w-6 rounded text-xs',
  md: 'h-10 w-10 rounded-xl text-lg',
} as const;

/**
 * A project's icon: its ORGANIZATION'S logo when there is one.
 *
 * Half of an agency's projects are called "Support Program". The project name
 * is therefore not what identifies a row — the client is — so the one piece of
 * visual identity a row gets should be the client's mark, not the first letter
 * of a word that repeats a dozen times down the page.
 *
 * Falls back to the project's initial when the organization has no logo, which
 * is what every row used to show.
 */
export function ProjectIcon({
  project,
  size = 'md',
  muted = false,
  className,
}: {
  project: Parameters<typeof projectOrg>[0] & { name?: string };
  size?: keyof typeof SIZES;
  /** Archived projects wear grey rather than the brand ramp. */
  muted?: boolean;
  className?: string;
}) {
  const org = projectOrg(project);
  const initial = project.name?.[0] ?? '?';

  if (org?.logo) {
    return (
      <img
        src={org.logo}
        // Decorative: the organization is named in text beside every use of
        // this, so announcing it here would just repeat it.
        alt=""
        className={cn(
          SIZES[size],
          'shrink-0 border border-grey-200 bg-white object-contain',
          className,
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        SIZES[size],
        'flex shrink-0 items-center justify-center font-bold text-white',
        muted ? 'bg-grey-400' : 'bg-gradient-to-br from-primary to-primary-600',
        className,
      )}
      aria-hidden
    >
      {initial}
    </div>
  );
}
